// Radha — Account Aggregator bank-feed reads + writes, direct path.
//
// Consent creation goes through aa-create-consent (needs SETU_CLIENT_SECRET,
// which the browser must never hold); the customer approves in their own AA
// app; aa-webhook is the only writer of a consent's approved/rejected status
// (migration 048), mirroring how payments works (047). Once a consent is
// active, aa-fetch-data pulls transactions straight into bank_transactions.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface AaConsent {
  id: string; subject_label: string; customer_mobile: string;
  fi_types: string[]; date_from: string; date_to: string;
  tsp_provider: string; consent_handle: string | null; consent_id: string | null;
  status: "requested" | "pending" | "active" | "paused" | "revoked" | "rejected" | "expired";
  created_at: string;
}

export function useAaConsents() {
  return useQuery({
    queryKey: ["aa-consents"],
    queryFn: async (): Promise<AaConsent[]> => {
      const { data, error } = await supabase
        .from("aa_consents")
        .select("id, subject_label, customer_mobile, fi_types, date_from, date_to, tsp_provider, consent_handle, consent_id, status, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export interface AaFetchSession {
  id: string; consent_id: string; status: "pending" | "completed" | "failed";
  txns_imported: number; error_detail: string | null; fetched_at: string | null; created_at: string;
}

export function useAaFetchSessions() {
  return useQuery({
    queryKey: ["aa-fetch-sessions"],
    queryFn: async (): Promise<AaFetchSession[]> => {
      const { data, error } = await supabase
        .from("aa_fetch_sessions")
        .select("id, consent_id, status, txns_imported, error_detail, fetched_at, created_at")
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export interface CreateConsentInput {
  subject_label: string; customer_mobile: string;
  date_from: string; date_to: string; fi_types?: string[];
}

export function useCreateAaConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateConsentInput) => {
      const { data, error } = await supabase.functions.invoke("aa-create-consent", { body: input });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data as AaConsent;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aa-consents"] }),
  });
}

export function useAaFetchData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (consentId: string) => {
      const { data, error } = await supabase.functions.invoke("aa-fetch-data", { body: { consent_id: consentId } });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data as { session_id: string; imported: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aa-fetch-sessions"] });
      qc.invalidateQueries({ queryKey: ["recon-direct"] });
    },
  });
}

export function aaErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
